import { useState, useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { StripePayment } from "@/components/payment/StripePayment";
import { DeliveryToggle } from "@/components/DeliveryToggle";
import { CheckoutSummary } from "@/components/checkout/CheckoutSummary";
import { DeliveryInformation } from "@/components/checkout/DeliveryInformation";
import { ContactInformation } from "@/components/checkout/ContactInformation";
import { supabase } from "@/integrations/supabase/client";

const Checkout = () => {
  const {
    items,
    total,
    deliveryTotal,
    processingFee,
    clearCart
  } = useCart();
  const navigate = useNavigate();
  const [deliveryType, setDeliveryType] = useState<"delivery" | "pickup">("pickup");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadProfile = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (user) {
        const {
          data: profile
        } = await supabase.from('profiles').select('*').eq('id', user.id).single();
        if (profile) {
          setFirstName(profile.first_name || "");
          setLastName(profile.last_name || "");
          setAddress(profile.address || "");
          setCity(profile.city || "");
          setState(profile.state || "");
          setPostalCode(profile.postal_code || "");
        }
      }
      setIsLoading(false);
    };
    loadProfile();
  }, []);
  
  const handlePaymentSuccess = async (orderId: string) => {
    try {
      console.log("Payment successful for order:", orderId);
      clearCart();
      
      const { data: order, error } = await supabase
        .from('orders')
        .select('status, payment_status')
        .eq('id', orderId)
        .single();
        
      if (error) {
        console.error("Error fetching order after payment:", error);
      } else {
        console.log("Order status after payment:", order);
      }
      
      toast({
        title: "Payment Successful",
        description: "Thank you for your order!"
      });
      
      navigate(`/order-confirmation?orderId=${orderId}`);
    } catch (err) {
      console.error("Error in handlePaymentSuccess:", err);
      toast({
        title: "Order Placed",
        description: "Your payment was successful, but we encountered an issue updating your order. Please check your order history.",
        variant: "destructive"
      });
      navigate("/");
    }
  };
  
  const isDeliveryAvailable = items.every(item => item.delivery_available);

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }
  
  if (isLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      Loading...
    </div>;
  }
  
  const finalDeliveryFee = deliveryType === "delivery" ? deliveryTotal : 0;
  const finalTotal = total + finalDeliveryFee + processingFee;
  
  console.log(`Checkout - Current delivery type: ${deliveryType}, with ${items.length} items and deliveryTotal: ${deliveryTotal}, processingFee: ${processingFee}`);
  
  const handleDeliveryTypeChange = (value: string) => {
    const newType = value as "delivery" | "pickup";
    console.log(`Changing delivery type from ${deliveryType} to ${newType}`);
    setDeliveryType(newType);
  };
  
  return <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Checkout</h1>
            <p className="text-muted-foreground">Complete your order</p>
          </div>

          <div className="bg-card rounded-lg p-6 space-y-6">
            {isDeliveryAvailable && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4">Delivery Method</h2>
                  <DeliveryToggle 
                    value={deliveryType} 
                    onValueChange={handleDeliveryTypeChange}
                    deliveryAvailable={isDeliveryAvailable}
                  />
                </div>
              </>
            )}

            <ContactInformation 
              firstName={firstName} 
              lastName={lastName} 
              phone={phone} 
              deliveryType={deliveryType} 
              onFirstNameChange={e => setFirstName(e.target.value)} 
              onLastNameChange={e => setLastName(e.target.value)} 
              onPhoneChange={e => setPhone(e.target.value)} 
            />

            {deliveryType === "delivery" && <DeliveryInformation 
              address={address} 
              city={city} 
              postalCode={postalCode} 
              specialInstructions={specialInstructions} 
              onAddressChange={e => setAddress(e.target.value)} 
              onCityChange={e => setCity(e.target.value)} 
              onPostalCodeChange={e => setPostalCode(e.target.value)} 
              onSpecialInstructionsChange={e => setSpecialInstructions(e.target.value)} 
            />}
          </div>

          <CheckoutSummary 
            items={items} 
            total={total} 
            deliveryFee={finalDeliveryFee} 
            finalTotal={finalTotal} 
            deliveryType={deliveryType}
            processingFee={processingFee}
          />

          <div className="bg-card rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Payment Details</h2>
            <StripePayment 
              amount={finalTotal} 
              items={items.map(item => ({
                ...item,
                processing_fee: processingFee
              }))}
              onSuccess={handlePaymentSuccess} 
              orderDetails={{
                deliveryType,
                address: deliveryType === "delivery" ? {
                  street: address,
                  city,
                  state,
                  postalCode
                } : null,
                specialInstructions,
                contact: {
                  firstName,
                  lastName,
                  phone
                }
              }} 
            />
          </div>
        </div>
      </div>
    </div>;
};

export default Checkout;
